import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaTags,
  FaList,
  FaTable
} from 'react-icons/fa';

const HTMLLesson2 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const sections = [
    {
      title: "Understanding HTML Tags",
      content: "HTML tags are element names surrounded by angle brackets that define the structure and content of web pages",
      tips: [
        "The start tag is also called the opening tag",
        "The end tag is called the closing tag"
      ],
      icon: <FaTags className="section-icon" />,
      color: "#2563eb"
    },
    {
      title: "Tag Structure & Syntax", 
      content: "Learn how HTML tags work in pairs and the proper syntax for using them",
      keyPoints: [
        "HTML tags normally come in pairs like <p> and </p>",
        "The first tag in a pair is the start tag, the second is the end tag",
        "The end tag has a forward slash before the tag name",
        "Some tags are self-closing like <br />"
      ],
      icon: <FaList className="section-icon" />,
      color: "#1d4ed8"
    }
  ];

  const htmlTags = [
    { tag: "<!DOCTYPE>", description: "Defines the document type" },
    { tag: "<!--...-->", description: "Defines a comment" },
    { tag: "<a></a>", description: "Defines a link" },
    { tag: "<abbr></abbr>", description: "Defines an abbreviation or acronym" },
    { tag: "<address></address>", description: "Defines contact information for author/owner" },
    { tag: "<area></area>", description: "Defines an area inside an image map" },
    { tag: "<article></article>", description: "Defines an article" },
    { tag: "<aside></aside>", description: "Defines content aside from page content" },
    { tag: "<audio></audio>", description: "Defines embedded sound content" },
    { tag: "<b></b>", description: "Defines bold text" },
    { tag: "<blockquote></blockquote>", description: "Defines a quoted section from another source" },
    { tag: "<body></body>", description: "Defines the document's body" },
    { tag: "<br />", description: "Defines a single line break" },
    { tag: "<button></button>", description: "Defines a clickable button" },
    { tag: "<canvas></canvas>", description: "Used to draw graphics via scripting" },
    { tag: "<cite></cite>", description: "Defines the title of a work" },
    { tag: "<code></code>", description: "Defines a piece of computer code" },
    { tag: "<col></col>", description: "Specifies column properties within <colgroup>" },
    { tag: "<colgroup></colgroup>", description: "Specifies a group of columns for formatting" },
    { tag: "<data></data>", description: "Adds machine-readable translation of content" },
    { tag: "<datalist></datalist>", description: "Specifies pre-defined options for input" },
    { tag: "<del></del>", description: "Defines text deleted from a document" },
    { tag: "<dfn></dfn>", description: "Specifies a term to be defined" },
    { tag: "<div></div>", description: "Defines a section in a document" },
    { tag: "<dl></dl>", description: "Defines a description list" },
    { tag: "<dt></dt>", description: "Defines a term in a description list" },
    { tag: "<em></em>", description: "Defines emphasized text" },
    { tag: "<header></header>", description: "Defines a header for document or section" },
    { tag: "<hr>", description: "Defines a thematic change in content" }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 2;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 2 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Tags Reference</h1>
              <p className="html-lesson-subtitle">
                Master the building blocks of HTML - understand tags, syntax, and common elements
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {/* Section 1: Understanding HTML Tags */}
            <div 
              key={0}
              ref={(el) => addSectionRef(el, 0)}
              className={`html-content-card ${viewedSections.has(0) ? 'html-viewed' : ''} ${0 === activeSection ? 'html-active' : ''}`}
            >
              <div className="html-card-badge">
                {viewedSections.has(0) ? '✓ Viewed' : 'Introduction'}
              </div>
              
              <div className="html-card-header">
                <div className="html-card-icon" style={{ backgroundColor: sections[0].color }}>
                  {sections[0].icon}
                </div>
                <div className="html-card-text">
                  <h2 className="html-card-title">{sections[0].title}</h2>
                  <p className="html-card-content">{sections[0].content}</p>
                  
                  <div className="html-tips">
                    <h4>Quick Tips:</h4>
                    <ul>
                      {sections[0].tips.map((tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="html-card-actions">
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-action-btn"
                >
                  <FaPlay className="html-btn-icon" />
                  Watch Video Explanation
                </a>
              </div>
            </div>

            {/* Section 2: Tag Structure */}
            <div 
              key={1}
              ref={(el) => addSectionRef(el, 1)}
              className={`html-content-card ${viewedSections.has(1) ? 'html-viewed' : ''} ${1 === activeSection ? 'html-active' : ''}`}
            >
              <div className="html-card-badge">
                {viewedSections.has(1) ? '✓ Viewed' : 'Syntax'}
              </div>
              
              <div className="html-card-header">
                <div className="html-card-icon" style={{ backgroundColor: sections[1].color }}>
                  {sections[1].icon}
                </div>
                <div className="html-card-text">
                  <h2 className="html-card-title">{sections[1].title}</h2>
                  <p className="html-card-content">{sections[1].content}</p>
                  
                  <div className="html-key-points">
                    <h4>Key Points:</h4>
                    <ul>
                      {sections[1].keyPoints.map((point, idx) => (
                        <li key={idx} dangerouslySetInnerHTML={{ __html: point }} />
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="html-card-actions">
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-action-btn"
                >
                  <FaPlay className="html-btn-icon" />
                  Watch Video Tutorial
                </a>
              </div>
            </div>
          </div>

          {/* HTML Tags Reference Table */}
          <div 
            className="html-tags-reference"
            ref={(el) => addSectionRef(el, sections.length)}
          >
            <div className="html-tags-header">
              <div className="html-tags-badge">
                <FaTable className="html-badge-icon" />
                HTML Tags Reference
              </div>
              <h3 className="html-tags-title">
                Common HTML Tags and Their Descriptions
              </h3>
            </div>
            
            <div className="html-tags-table-container">
              <table className="html-tags-table">
                <thead>
                  <tr>
                    <th>HTML Tag</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {htmlTags.map((tag, index) => (
                    <tr key={index}>
                      <td className="html-tag-code">
                        <code>{tag.tag}</code>
                      </td>
                      <td className="html-tag-description">
                        {tag.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="html-tags-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Tags Tutorial
              </a>
            </div>
          </div>

          {/* Code Example Section */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Live Example
              </div>
              <h3 className="html-code-title">
                See HTML Tags in Action
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={`<!DOCTYPE html>
<html>
<head>
    <title>HTML Tags Example</title>
</head>
<body>
    <header>
        <h1>Welcome to My Website</h1>
        <nav>
            <a href="#home">Home</a> | 
            <a href="#about">About</a> | 
            <a href="#contact">Contact</a>
        </nav>
    </header>
    
    <main>
        <article>
            <h2>Article Title</h2>
            <p>This is a <strong>paragraph</strong> with <em>emphasized</em> text.</p>
            <button>Click Me</button>
        </article>
        
        <aside>
            <h3>Related Links</h3>
            <ul>
                <li><a href="#link1">Link 1</a></li>
                <li><a href="#link2">Link 2</a></li>
            </ul>
        </aside>
    </main>
    
    <footer>
        <p>&copy; 2024 My Website</p>
    </footer>
</body>
</html>`}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Full Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML Tags?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned about HTML tags and their syntax. Practice using different tags to build web pages!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-paragraphs" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more help? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson2;